import sanitizeHtml from 'sanitize-html'
import { nameCase } from '@foundernest/namecase'

export function sanitizeString(string: string): string {
  if (typeof string !== 'string' || string.length === 0) {
    return string
  }
  // Remove all HTML tags
  return sanitizeHtml(string, {
    allowedTags: [],
    allowedAttributes: {},
  })
}

export function capitalizeFirstLetter(string) {
  if (typeof string !== 'string' || string.length === 0) {
    return string
  }

  return string[0].toUpperCase() + string.slice(1)
}

export function spaceOutPhoneDigits(string: string): string {
  return string
    .replace(/[^0-9]/gm, '') // Remove special chars
    .replace(/[0-9]/gm, '$& ') // Add a space between chars
}

/**
 * Sanitizes a string to be safe for use in filenames by removing or replacing invalid characters
 * @param input The string to sanitize
 * @returns A filename-safe string
 */
export function sanitizeFilename(input: string): string {
  if (!input) return ''

  // Replace characters that are problematic in filenames
  return input
    .trim()
    .replace(/[\\/:*?"<>|'`~!@#$%^&+={}[\]]/g, '') // Remove invalid filename chars
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/--+/g, '-') // Replace multiple hyphens with a single one
    .replace(/^-|-$/g, '') // Remove leading/trailing hyphens
}

/**
 * Normalizes a name by properly capitalizing it according to standard name capitalization rules.
 *
 * This function uses the @foundernest/namecase library which handles various name capitalization
 * patterns including:
 * - Standard first letter capitalization (daniel → Daniel)
 * - Special prefixes like Mc/Mac (mcDonalds → McDonalds)
 * - Compound names with hyphens (LEIGH-WILLIAMS → Leigh-Williams)
 * - Names with apostrophes (O'CALLAGHAN → O'Callaghan)
 * - Preserving capitalization in names like "TestCo" → "TestCo"
 *
 * @param name The name to normalize
 * @returns The normalized name
 */
export function normalizeName(
  name: string | null | undefined
): string | null | undefined {
  if (typeof name !== 'string' || name.length === 0) {
    return name
  }

  // First apply the nameCase library's normalization
  const normalized = nameCase(name)

  // Special case for company-like names with internal capitalization (e.g., "TestCo")
  // If the original name already had the pattern of first letter capitalized and
  // an internal capital letter, preserve it exactly as is
  if (/^[A-Z][a-z]+[A-Z][a-z]*$/.test(name)) {
    return name
  }

  return normalized
}

/**
 * Converts a PascalCase or camelCase string to a readable format with spaces
 * Example: "PascalCase" → "Pascal Case"
 * Example: "camelCase" → "Camel Case"
 *
 * @param str The PascalCase or camelCase string to format
 * @returns A human-readable string with spaces
 */
export function formatPascalCaseToReadable(
  str: string | null | undefined
): string {
  if (!str) return ''

  // Add space before capital letters and trim any leading space
  return str.replace(/([A-Z])/g, ' $1').trim()
}

/**
 * Removes diacritical marks (accents) from a string by decomposing characters
 * into base + combining marks (NFD) and stripping the marks.
 *
 * Example: "Vía" → "Via", "Bayamón" → "Bayamon"
 */
export function stripDiacritics(str: string | null | undefined): string {
  if (!str) return ''

  return str.normalize('NFD').replace(/\p{Diacritic}/gu, '')
}

// Credit bureaus reject accented residence addresses (e.g. "Vía") as mismatches.
export function stripAddressDiacritics<T extends string | null | undefined>(
  value: T
): T {
  return (value ? stripDiacritics(value) : value) as T
}

export function stripAddressRowDiacritics<T extends Record<string, any>>(
  address: T
): T {
  return {
    ...address,
    street: stripAddressDiacritics(address.street),
    apt: stripAddressDiacritics(address.apt),
    city: stripAddressDiacritics(address.city),
  }
}

/**
 * Converts a snake_case string to a readable format with spaces and capitalization
 * Example: "snake_case" → "Snake Case"
 *
 * @param str The snake_case string to format
 * @returns A human-readable string with spaces and proper capitalization
 */
export function formatSnakeCaseToReadable(
  str: string | null | undefined
): string {
  if (!str) return ''

  // Split by underscore, capitalize first letter of each word, join with spaces
  return str
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}
